/**
 * B130 Wave 0 loader (Day 55 audit, Day 56 implementation).
 *
 * Single entry point for every tax rule lookup. The Wave 1 pattern:
 *
 *   1. Per-FY rules files (`tax-rules-2025-26.ts`,
 *      `state-tax-rules-2025-26.ts`) call `registerFY` at module init
 *      with their `TaxRule[]` array.
 *   2. Helpers call `getTaxRule({category, jurisdiction, fy?})` to
 *      resolve a single rule, or `listTaxRules()` for the user-facing
 *      tab (B73 / Wave 5).
 *   3. Missing rule → throw in dev (catches typos before deploy);
 *      `console.error` + return `null` in prod (caller surfaces UI
 *      banner). Never silent.
 *
 * Wave 1 keeps the legacy `CURRENT_TAX_RULES` / `LAND_TAX_RULES`
 * exports in place as backwards-compatibility shims so
 * `calculations.ts` / `TaxPage.tsx` continue to read the old shape
 * unchanged. Wave 6 deletes the shims and swaps every helper to read
 * via this loader.
 *
 * See: `docs/audits/B130-audit.md`, CLAUDE.md "Tax Document as Single
 * Source of Truth".
 */

import type { FY, Jurisdiction, TaxRule, TaxRuleCategory } from "./tax-rules-types";

// Module-level registry. Populated as per-FY rules files import this
// loader and call `registerFY` at the top of their own module.
const RULES_BY_FY = new Map<FY, TaxRule[]>();

/**
 * Per-FY rules files call this at module init to register their rule
 * array. **Additive + idempotent** — multiple calls for the same FY
 * merge by `rule_id`: a later call with the same `rule_id` replaces the
 * earlier; new `rule_id`s append. This lets federal + state rules files
 * each call `registerFY("2025-26", […])` independently and have their
 * union visible to the loader.
 */
export function registerFY(fy: FY, rules: TaxRule[]): void {
  const existing = RULES_BY_FY.get(fy) ?? [];
  const byId = new Map<string, TaxRule>();
  for (const r of existing) byId.set(r.rule_id, r);
  for (const r of rules) byId.set(r.rule_id, r);
  RULES_BY_FY.set(fy, Array.from(byId.values()));
}

/**
 * Resolve the current Australian financial year from a date.
 * Australian FY runs 1 July → 30 June.
 *
 * - Jan – Jun → previous calendar year's FY (e.g. 3 May 2026 → "2025-26")
 * - Jul – Dec → current calendar year's FY (e.g. 3 Aug 2026 → "2026-27")
 *
 * Defaults to "now" when called without a date.
 */
export function getCurrentFY(today: Date = new Date()): FY {
  const month = today.getMonth(); // 0-indexed (Jan = 0, Jun = 5, Jul = 6, Dec = 11)
  const year = today.getFullYear();
  if (month <= 5) {
    // Jan–Jun → previous calendar year's FY
    const startYear = year - 1;
    const endYear = year;
    return `${startYear}-${String(endYear).slice(2)}` as FY;
  }
  // Jul–Dec → current calendar year's FY
  const startYear = year;
  const endYear = year + 1;
  return `${startYear}-${String(endYear).slice(2)}` as FY;
}

/**
 * Resolve a single tax rule. Throws in dev when the rule isn't loaded
 * (catches typos + missing FY rolls before deploy). Returns `null` in
 * prod with a `console.error`; caller is responsible for UI fallback
 * ("This calculation needs a tax rule we haven't loaded yet").
 *
 * Multiple rules can share the same `(category, jurisdiction, fy)`
 * triple if they discriminate by `rule_id` (e.g. MLS singles vs MLS
 * family — both encoded as a single ConditionalRule in Wave 1, but
 * future Waves may split them). When multiple match, returns the first
 * — caller relies on `listTaxRules()` for full enumeration.
 */
export function getTaxRule(opts: {
  category: TaxRuleCategory;
  jurisdiction: Jurisdiction;
  fy?: FY;
}): TaxRule | null {
  const fy = opts.fy ?? getCurrentFY();
  const rules = RULES_BY_FY.get(fy) ?? [];
  const match = rules.find(
    (r) => r.category === opts.category && r.jurisdiction === opts.jurisdiction,
  );
  if (!match) {
    const msg = `Tax rule not loaded: ${opts.category}/${opts.jurisdiction}/${fy}`;
    if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
      throw new Error(msg);
    }
    // Prod: log loudly + return null. Caller decides UI banner.
     
    console.error(msg);
    return null;
  }
  return match;
}

/**
 * List all rules matching the optional filters. Used by the B73
 * (Wave 5) user-facing "Tax Rules" tab to render every rule the app
 * applies, with citation traceability.
 */
export function listTaxRules(opts: {
  category?: TaxRuleCategory;
  jurisdiction?: Jurisdiction;
  fy?: FY;
} = {}): TaxRule[] {
  const fyFilter = opts.fy;
  const all: TaxRule[] = [];
  for (const [fy, rules] of RULES_BY_FY.entries()) {
    if (fyFilter && fy !== fyFilter) continue;
    for (const r of rules) {
      if (opts.category && r.category !== opts.category) continue;
      if (opts.jurisdiction && r.jurisdiction !== opts.jurisdiction) continue;
      all.push(r);
    }
  }
  return all;
}

/**
 * Dev-only check. Throws when any registered rule's `last_reviewed`
 * is older than `maxAgeMonths` (default 13). Run as part of the dev
 * test suite to catch missed annual reviews before they ship to prod.
 */
export function assertNoStaleRules(opts: { maxAgeMonths?: number } = {}): void {
  const maxAgeMonths = opts.maxAgeMonths ?? 13;
  const cutoffMs = Date.now() - maxAgeMonths * 30 * 24 * 60 * 60 * 1000;
  const stale: string[] = [];
  for (const rules of RULES_BY_FY.values()) {
    for (const r of rules) {
      const reviewed = Date.parse(r.last_reviewed);
      if (!Number.isFinite(reviewed)) {
        stale.push(`${r.rule_id} (invalid last_reviewed: ${r.last_reviewed})`);
        continue;
      }
      if (reviewed < cutoffMs) {
        stale.push(`${r.rule_id} (last_reviewed ${r.last_reviewed}, > ${maxAgeMonths}mo old)`);
      }
    }
  }
  if (stale.length > 0) {
    throw new Error(`Stale tax rules detected:\n  ${stale.join("\n  ")}`);
  }
}

/**
 * Test helper — clear the registry between test files. NOT for
 * production use.
 */
export function _clearRegistryForTests(): void {
  RULES_BY_FY.clear();
}
